import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  demoMemberId,
  demoNow,
  demoPerspective,
  demoSettings,
} from '@troyai/portal/investor-demo/core';
import { ButtonComponent } from '@troyai/ui-kit';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';

import { demoOff, jumpThreeDays, resetDemo } from '../demo-commands';
import { DemoStore } from '../demo-store';

/**
 * The presenter's hidden controls: a 16 px, near-invisible button in the bottom-left corner that
 * opens a small card, plus Alt+Shift+R (reset) and +J (+3 days) from anywhere, drawers included.
 * Mounted by the app shell only while demo mode is on.
 */
@Component({
  selector: 't-demo-controls',
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      aria-label="Demo controls"
      class="bg-grey-60 fixed bottom-2 left-2 z-[1000] size-4 rounded-full opacity-10 hover:opacity-60 focus-visible:opacity-60"
      [class.opacity-60]="open"
      [attr.aria-expanded]="open"
      (click)="open = !open"></button>
    @if (open) {
      <div
        class="border-grey-30 text-grey-90 fixed bottom-8 left-2 z-[1000] flex w-[220px] flex-col gap-2 rounded-lg border bg-white p-3 shadow-lg"
        role="group"
        aria-label="Demo controls">
        <div class="text-grey-60 text-xs">Demo date: {{ demoDate }}</div>
        <div class="text-grey-60 text-xs">
          Member {{ memberId }}{{ isScriptedMember ? ' (scripted)' : '' }}
        </div>
        <div class="text-grey-60 text-xs">Perspective: {{ perspective }}</div>
        <t-button variant="secondary" size="small" [isFullWidth]="true" (onClick)="reset()">
          Reset
        </t-button>
        <t-button
          variant="secondary"
          size="small"
          [isFullWidth]="true"
          [disabled]="jumped"
          (onClick)="jump()">
          +3 days
        </t-button>
        <t-button variant="neutral" size="small" [isFullWidth]="true" (onClick)="off()">
          Demo off
        </t-button>
      </div>
    }
  `,
})
export class DemoControlsComponent {
  private readonly store = inject(Store);
  private readonly demo = inject(DemoStore);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  open = false;

  constructor() {
    // Loads the store now: a "+3 days" flag left from the day before is cleared on the first load,
    // and that must happen before the date shows, not at the first request about the member.
    this.demo.data();
  }

  get demoDate(): string {
    return format(demoNow(), 'EEE, MMM d');
  }

  get jumped(): boolean {
    return demoSettings().jumped;
  }

  get perspective(): string {
    return demoPerspective() === 'pharmacy' ? 'Pharmacy' : 'Care';
  }

  get memberId(): number {
    return demoMemberId();
  }

  /** True while no member was chosen by the URL switch, so the demo runs on the scripted one. */
  get isScriptedMember(): boolean {
    return demoSettings().memberId === null;
  }

  /**
   * Escape closes the card. The shortcuts match on `event.code`, because Alt changes `event.key`
   * on macOS, and ignore a held key, which would reset twice.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.open = false;
      return;
    }
    if (event.repeat || !event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey) {
      return;
    }
    switch (event.code) {
      case 'KeyR':
        event.preventDefault();
        this.reset();
        break;
      case 'KeyJ':
        event.preventDefault();
        this.jump();
        break;
    }
  }

  reset(): void {
    resetDemo(this.demo);
  }

  jump(): void {
    if (demoSettings().jumped) {
      return;
    }
    this.open = false;
    jumpThreeDays({ store: this.store, demo: this.demo, router: this.router });
    this.toastr.success(`Jumped to ${format(demoNow(), 'EEE, MMM d')}`);
  }

  off(): void {
    demoOff();
  }
}
